import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, map, take } from 'rxjs';

import { ApConfig } from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

// VPDW-763: accounting systems that support outbound vendor creation. Mirrors the
// vive-v2 create-loop allow-list (clientRegisteredVendor.js / exemptVendor.js);
// only these are offered when designating where a vendor is created.
const CREATE_ELIGIBLE_AP_SYSTEMS: readonly string[] = [
  'Cinc',
  'Vantaca',
  'Yardi',
  'Caliber',
  'MRI',
  'RentManager',
  'AppFolio'
];

/**
 * VPDW-763: builds a stable, human-readable `label -> ApConfig.id` map for the
 * accounting-system selector (shared by the pop-up and the bulk-upload field).
 * Falls back to `apSystem` when no display name is set, and disambiguates duplicate
 * labels with the config id.
 */
export function buildApConfigSelectionMap(
  apConfigs: ApConfig[]
): Map<string, number> {
  const map = new Map<string, number>();

  (apConfigs ?? []).forEach((apConfig: ApConfig) => {
    const { id, apDisplayName, apSystem } = apConfig;

    if (!id) {
      return;
    }

    const baseLabel =
      (apDisplayName && apDisplayName.trim()) ||
      apSystem ||
      'Accounting System';
    const label = map.has(baseLabel) ? `${baseLabel} (#${id})` : baseLabel;

    map.set(label, id);
  });

  return map;
}

@Injectable({
  providedIn: 'root'
})
export class ApConfigService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getApConfigCount(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/config/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get('/api/v2/admin/ap/config/count', { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getApConfigs(query: {} = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ApConfig[]>;
        } = {
          Admin: () =>
            this.http.get<ApConfig[]>('/api/v2/admin/ap/configs', {
              params: query
            }),
          Client: () =>
            this.http.get<ApConfig[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/configs`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  /**
   * VPDW-763: returns the client's accounting systems a vendor can be created in —
   * configs with `allowCreateVendors` enabled whose `apSystem` supports outbound
   * creation. Used to populate the "designate accounting system(s)" selector and to
   * decide whether the selector needs to be shown (only when 2+ are eligible).
   * Pass `CompanyId` for the Admin surface; the Client surface is auto-scoped.
   */
  getCreateEligibleApConfigs(CompanyId?: number): Observable<ApConfig[]> {
    const query = CompanyId ? { CompanyId } : {};

    return this.getApConfigs(query).pipe(
      map((apConfigs: ApConfig[]) =>
        (apConfigs ?? []).filter(
          (apConfig: ApConfig) =>
            apConfig.allowCreateVendors === true &&
            !!apConfig.id &&
            !!apConfig.apSystem &&
            CREATE_ELIGIBLE_AP_SYSTEMS.includes(apConfig.apSystem)
        )
      )
    );
  }

  getApConfig(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get('/api/v2/admin/ap/configs', { params: query }),
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/ap/config`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }
}
